"use client";

import { useEffect, useState } from "react";

type Theme = "light" | "dark";

export function ThemeToggle(): JSX.Element {
  const [theme, setTheme] = useState<Theme>("light");

  useEffect(() => {
    const stored = (typeof window !== "undefined" && localStorage.getItem("theme")) as Theme | null;
    const initial = stored ?? "light";
    applyTheme(initial);
    setTheme(initial);
  }, []);

  const applyTheme = (value: Theme): void => {
    const root = document.documentElement;
    if (value === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    localStorage.setItem("theme", value);
  };

  const toggle = (): void => {
    const next = theme === "light" ? "dark" : "light";
    setTheme(next);
    applyTheme(next);
  };

  return (
    <button
      type="button"
      onClick={toggle}
      className="flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition-colors hover:border-primary-200 hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
      aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      aria-pressed={theme === "dark"}
      data-testid="toggle-theme"
    >
      <span
        className={`h-3 w-3 rounded-full ${theme === "dark" ? "bg-primary-600" : "bg-slate-300"}`}
      />
      {theme === "dark" ? "Dark" : "Light"}
    </button>
  );
}
